'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { useMediaRemote } from '@vidstack/react';
import { usePlayer } from 'contexts/PlayerContext';
import { useAppDispatch, useAppSelector } from 'lib/hooks';

import {
  setVolume,
  toggleMuteAudio,
  selectIsMuted,
  selectVolume,
} from 'slices/mediaSlice';
import styles from './TopNavBar.module.scss';

const VolumeButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const { player } = usePlayer();
  const [isVolumeActive, setIsVolumeActive] = useState(false);

  const remote = useMediaRemote(player);

  const isMuted = useAppSelector(selectIsMuted);
  const volume = useAppSelector(selectVolume);

  const handleMute = () => {
    dispatch(toggleMuteAudio());
    remote.toggleMuted();
  };

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseInt(event.target.value) / 100;
    dispatch(setVolume(newVolume));
    remote.changeVolume(newVolume);
  };

  const handleMouseEnter = () => setIsVolumeActive(true);
  const handleMouseLeave = () => setIsVolumeActive(false);
  const handleFocus = () => setIsVolumeActive(true);
  const handleBlur = () => setIsVolumeActive(false);

  const getSpeakerIcon = () => {
    if (isMuted || volume === 0) return '/Speaker-Mute.svg';
    if (volume <= 0.3) return '/SpeakerLow.svg';
    return '/SpeakerHigh.svg';
  };

  // Don't render if no player is available
  if (!player) {
    return null;
  }

  return (
    <button
      aria-label="mute-button"
      aria-pressed={isMuted || volume === 0}
      className={`${styles.iconButton} ${
        isVolumeActive ? styles.volumeButtonActive : ''
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <div>
        <Image
          onClick={handleMute}
          src={getSpeakerIcon()}
          alt="Speaker"
          width={24}
          height={24}
        />
        {isVolumeActive && (
          <input
            aria-label="volume-slider"
            max={100}
            min={0}
            onChange={handleVolumeChange}
            type="range"
            value={volume * 100}
          />
        )}
      </div>
    </button>
  );
};

export default VolumeButton;
